  /**
   * Personalisation Drawer
   * A beautiful, simplified personalization experience
   */

  // API endpoints for positioning data from mono app
  const COORDINATES_API = 'https://mono.homebodii.com/api/view_coordinates';
  const PREVIEW_API = 'https://mono.homebodii.com/api/preview';
  const SHOP_DOMAIN = 'homebodii-uat.myshopify.com';

  // Use XMLHttpRequest instead of fetch to bypass Shopify's fetch interceptor
  // which adds headers that trigger CORS preflight failures
  function xhrGet(url) {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open('GET', url, true);
      xhr.onload = function () {
        if (xhr.status >= 200 && xhr.status < 300) {
          try {
            resolve({ ok: true, status: xhr.status, data: JSON.parse(xhr.responseText) });
          } catch (e) {
            reject(new Error('Invalid JSON response'));
          }
        } else {
          resolve({ ok: false, status: xhr.status, data: null });
        }
      };
      xhr.onerror = function () {
        reject(new Error('Network error'));
      };
      xhr.timeout = 15000;
      xhr.ontimeout = () => reject(new Error('The preview service timed out. Please try again.'));
      xhr.send();
    });
  }

  class PersonalisationDrawer {
    constructor() {
      this.drawer = document.getElementById('personalisation-drawer');
      if (!this.drawer) {
        return;
      }

      this.savedDesigns = [];
      this.currentStep = 1;
      this.apiCoordinates = null; // Will store API coordinates response
      this.apiAvailableThreads = null; // Will store available thread colors from API
      this.useApiPlacements = true; // Enable API-based placements
      
      // Get total steps from data attribute (2 if no placement step, 3 if placement step)
      const dataInput = document.getElementById('personalisation-data');
      this.totalSteps = dataInput ? parseInt(dataInput.dataset.totalSteps) || 3 : 3;
      this.productId = dataInput?.dataset.productId || null;
      
      
      // Check for single placement
      const singlePlacement = document.getElementById('single-placement');
      this.hasSinglePlacement = !!singlePlacement;
      
      // Selections
      this.selections = {
        text: '',
        font: '',
        fontFamily: '',
        fontCode: '', // API font code
        color: '',
        colorHex: '',
        placement: singlePlacement ? singlePlacement.value : '',
        placementCode: '', // Area code from API
        charLimit: 12, // Will be updated from API
        lineLimit: 1 // Will be updated from API
      };
      
      // Store current placement's allowed options (from API)
      this.currentPlacementData = {
        allowedFonts: [],
        allowedEmojis: [],
        charLimit: 12,
        lineLimit: 1
      };

      // Cache DOM elements
      this.cacheElements();
      this.bindEvents();
      this.initializeDefaults();
      this.initFloatingPreviewButton();
      this.initScrollIndicator();
      
      // Fetch placements from API
      if (this.useApiPlacements && this.productId) {
        // Hide local overlay - API will render text on the image
        if (this.previewOverlay) {
          this.previewOverlay.style.display = 'none';
        }
        this.fetchPlacements();
      } else {
      }
    }

    cacheElements() {
      // Main elements
      this.overlay = this.drawer.querySelector('.personalisation-drawer__overlay');
      this.panel = this.drawer.querySelector('.personalisation-drawer__panel');
      this.closeButtons = this.drawer.querySelectorAll('[data-close-drawer]');
      
      // Progress
      this.progressSteps = this.drawer.querySelectorAll('.progress-step');
      this.progressLines = this.drawer.querySelectorAll('.progress-step__line');
      
      // Panels
      this.panels = this.drawer.querySelectorAll('.step-panel');
      
      // Step 1: Text
      this.textInput = this.drawer.querySelector('[data-text-input]');
      this.charCount = this.drawer.querySelector('[data-char-count]');
      this.charLimit = this.drawer.querySelector('[data-char-limit]');
      this.suggestionChips = this.drawer.querySelectorAll('[data-suggestion]');
      this.emojiButtons = this.drawer.querySelectorAll('[data-emoji]');
      
      // Build list of allowed emojis from the buttons
      this.allowedEmojis = [];
      this.emojiButtons.forEach(btn => {
        if (btn.dataset.emoji) {
          this.allowedEmojis.push(btn.dataset.emoji);
        }
      });
      
      // Step 2: Font & Color
      this.fontOptions = this.drawer.querySelectorAll('[data-font]');
      this.colorOptions = this.drawer.querySelectorAll('[data-color]');
      
      // Step 3: Placement
      this.placementOptions = this.drawer.querySelectorAll('[data-placement]');
      
      // Preview
      this.previewText = this.drawer.querySelector('[data-preview-text]');
      this.previewOverlay = this.drawer.querySelector('[data-preview-overlay]');
      this.previewSummary = this.drawer.querySelector('[data-preview-summary]');
      
      // Navigation
      this.prevButton = this.drawer.querySelector('[data-prev-step]');
      this.nextButton = this.drawer.querySelector('[data-next-step]');
      this.addButton = this.drawer.querySelector('[data-add-personalisation]');
      
      // Data
      this.dataInput = document.getElementById('personalisation-data');
    }

    bindEvents() {
      this.drawer.querySelector('[data-another-placement]').addEventListener('click', () => {
        if (!this.previewValid || !this.selections.text.trim()) return;
        this.savedDesigns.push({...this.selections});
        this.renderSavedDesigns();
        this.selections.text = '';
        this.textInput.value = '';
        this.selections.placement = '';
        this.selections.placementCode = '';
        this.placementOptions.forEach(option => { option.classList.remove('active'); option.disabled = this.savedDesigns.some(d => d.placementCode === option.dataset.areaCode); });
        this.previewValid = false;
        this.goToStep(1);
      });
      // Close drawer
      this.closeButtons.forEach(btn => {
        btn.addEventListener('click', () => this.close());
      });

      // Close on escape key
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Tab' && this.isOpen()) {
          const controls = [...this.panel.querySelectorAll('button:not([disabled]), input:not([type="hidden"]), a[href]')].filter(el => el.getClientRects().length);
          const first = controls[0], last = controls[controls.length - 1];
          if (e.shiftKey && (document.activeElement === first || document.activeElement === this.panel)) { e.preventDefault(); last?.focus(); }
          else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first?.focus(); }
        }
        if (e.key === 'Escape' && this.isOpen()) {
          this.close();
        }
      });

      // Progress step clicks
      this.progressSteps.forEach(step => {
        step.addEventListener('click', () => {
          const stepNum = parseInt(step.dataset.step);
          if (stepNum <= this.currentStep) {
            this.goToStep(stepNum);
          }
        });
      });

      // Text input
      if (this.textInput) {
        this.textInput.addEventListener('input', () => this.handleTextInput());
        this.textInput.addEventListener('keydown', (e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            this.nextStep();
          }
        });
      }

      // Suggestion chips
      this.suggestionChips.forEach(chip => {
        chip.addEventListener('click', () => {
          const suggestion = chip.dataset.suggestion || chip.textContent.trim();
          // Only apply suggestion if it fits within character limit
          const charLimit = this.selections.charLimit || this.textInput.maxLength || 12;
          if (suggestion.length <= charLimit) {
            this.textInput.value = suggestion;
            this.handleTextInput();
          }
        });
      });

      // Emoji buttons
      this.emojiButtons.forEach(btn => {
        btn.addEventListener('click', () => this.insertEmoji(btn.dataset.emoji));
      });

      // Font options
      this.fontOptions.forEach(option => {
        option.addEventListener('click', () => this.selectFont(option));
      });

      // Color options
      this.colorOptions.forEach(option => {
        option.addEventListener('click', () => this.selectColor(option));
      });

      // Placement options
      this.placementOptions.forEach(option => {
        option.addEventListener('click', () => this.selectPlacement(option));
      });

      // Navigation buttons
      if (this.prevButton) {
        this.prevButton.addEventListener('click', () => this.prevStep());
      }
      if (this.nextButton) {
        this.nextButton.addEventListener('click', () => this.nextStep());
      }
      if (this.addButton) {
        this.addButton.addEventListener('click', () => this.addToCart());
      }

      // Listen for open trigger
      document.addEventListener('personalisation:open', () => this.open());
    }

    initializeDefaults() {
      // Set initial active states from data attributes
      const activeFont = this.drawer.querySelector('[data-font].active');
      if (activeFont) {
        this.selections.font = activeFont.dataset.font;
        this.selections.fontFamily = activeFont.dataset.fontFamily;
      } else {
        // Fallback to first font option
        const firstFont = this.drawer.querySelector('[data-font]');
        if (firstFont) {
          firstFont.classList.add('active');
          this.selections.font = firstFont.dataset.font;
          this.selections.fontFamily = firstFont.dataset.fontFamily;
        }
      }

      const activeColor = this.drawer.querySelector('[data-color].active');
      if (activeColor) {
        this.selections.color = activeColor.dataset.color;
        this.selections.colorHex = activeColor.dataset.hex;
      } else {
        // Fallback to first color option
        const firstColor = this.drawer.querySelector('[data-color]');
        if (firstColor) {
          firstColor.classList.add('active');
          this.selections.color = firstColor.dataset.color;
          this.selections.colorHex = firstColor.dataset.hex;
        }
      }

      // Only pre-select placement if there's a single (hidden) placement
      if (this.hasSinglePlacement) {
        const singlePlacement = document.getElementById('single-placement');
        if (singlePlacement) {
          this.selections.placement = singlePlacement.value;
        }
      }

      // Update preview image based on initial placement
      // Skip if using API placements - fetchPlacements() will handle this after loading correct positions
      if (!this.useApiPlacements) {
        this.updatePreviewImage();
      }
      
      // Update character limit based on initial placement
      this.updateCharLimit();

      // Update navigation for correct number of steps
      this.updateNavigation();
      this.updatePreview();
    }

    // -------------------------------------------------------------------------
    // Floating Preview Button
    // -------------------------------------------------------------------------

    initFloatingPreviewButton() {
      this.scrollContent = this.drawer.querySelector('.personalisation-drawer__content');
      this.previewCardEl = this.drawer.querySelector('.preview-card');
      if (!this.scrollContent || !this.previewCardEl) return;

      // Create floating button
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'preview-float-btn';
      btn.innerHTML = `
        <img class="preview-float-btn__thumb" alt="Preview" style="display:none">
        <span class="preview-float-btn__text">
          <span class="preview-float-btn__label">Click to view preview</span>
          <span class="preview-float-btn__summary"></span>
        </span>
        <svg class="preview-float-btn__arrow" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <path d="M12 19V5M5 12l7-7 7 7"/>
        </svg>
      `;

      // Insert BEFORE the scrollable content area (between progress and content)
      this.scrollContent.parentNode.insertBefore(btn, this.scrollContent);

      this.floatingBtn = btn;
      this.floatingThumb = btn.querySelector('.preview-float-btn__thumb');
      this.floatingSummary = btn.querySelector('.preview-float-btn__summary');

      // Click scrolls back to the preview
      btn.addEventListener('click', () => {
        this.previewCardEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    }

    initScrollIndicator() {
      if (!this.panel) return;

      const indicator = document.createElement('button');
      indicator.type = 'button';
      indicator.className = 'scroll-down-indicator';
      indicator.setAttribute('aria-label', 'Scroll down');
      indicator.innerHTML = `
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
          <path d="M12 5v14M19 12l-7 7-7-7"/>
        </svg>
      `;

      this.panel.appendChild(indicator);
      this.scrollIndicator = indicator;

      const footer = this.drawer.querySelector('.personalisation-drawer__footer');
      if (footer) {
        indicator.style.bottom = (footer.offsetHeight + 8) + 'px';
      } else {
        indicator.style.bottom = '160px';
      }

      indicator.addEventListener('click', () => {
        if (!this.scrollContent) return;
        const previewCard = this.drawer.querySelector('.preview-card');
        if (previewCard) {
          this.scrollContent.scrollTo({
            top: previewCard.offsetHeight + 24,
            behavior: 'smooth'
          });
        }
      });
    }

    checkScrollIndicator() {
      if (!this.scrollIndicator || !this.scrollContent) return;
      this.scrollIndicator.classList.toggle('is-hidden', this.scrollContent.scrollTop > 50);
    }

    startPreviewCheck() {
      this.stopPreviewCheck();
      this._previewCheckInterval = setInterval(() => this.checkPreviewVisibility(), 250);
    }

    stopPreviewCheck() {
      if (this._previewCheckInterval) {
        clearInterval(this._previewCheckInterval);
        this._previewCheckInterval = null;
      }
    }

    checkPreviewVisibility() {
      if (!this.scrollContent) return;

      const scrollTop = this.scrollContent.scrollTop;

      // Show floating preview button once user has scrolled past the preview image
      if (this.floatingBtn) {
        this.floatingBtn.classList.toggle('is-visible', scrollTop > 200);
      }

      // Hide scroll-down indicator once user starts scrolling
      this.checkScrollIndicator();
    }

    updateFloatingButton() {
      if (!this.floatingBtn) return;

      // Sync thumbnail with current preview image (hide if no valid src)
      const previewImg = this.drawer.querySelector('[data-preview-image]');
      if (previewImg && this.floatingThumb) {
        const src = previewImg.getAttribute('src') || '';
        const isValidImage = src && (src.startsWith('data:image/') || src.includes('cdn.shopify') || src.includes('.jpg') || src.includes('.png') || src.includes('.webp'));
        this.floatingThumb.style.display = isValidImage ? '' : 'none';
        if (isValidImage) {
          this.floatingThumb.src = src;
        }
      }

      // Update summary text
      if (this.floatingSummary) {
        const text = this.selections.text || 'HB';
        const font = this.selections.font ? this.capitalizeFirst(this.selections.font) : '';
        const color = this.selections.color || '';
        this.floatingSummary.textContent = `"${text}" ${font} ${color}`.trim();
      }
    }

    // -------------------------------------------------------------------------
    // API Integration - Fetch Placements
    // -------------------------------------------------------------------------

    async fetchPlacements() {
      this.ready = false;
      try {
        const result = await xhrGet(`${COORDINATES_API}?shop=${SHOP_DOMAIN}&product_id=${this.productId}`);
        if (!result.ok || !Array.isArray(result.data?.coordinates)) return;
        const data = result.data;
        this.apiCoordinates = window.HBPersonalisationRules.placements(data.coordinates, this.dataInput.dataset.productTitle + ' ' + this.dataInput.dataset.productType);
        this.apiAvailableThreads = (data.available_threads || []).filter(t => t.colour_name.toLowerCase() !== 'chrome').map(t => ({...t, is_enabled: 1}));
        if (!this.apiCoordinates.length || !this.apiAvailableThreads.length || this.dataInput.dataset.feeAvailable !== 'true') return;
        this.updateColorOptionsFromApi(this.apiAvailableThreads);
        this.renderApiPlacements(this.apiCoordinates);
        this.ready = true;
        document.querySelectorAll('personalisation-trigger').forEach(el => { el.hidden = false; });
        document.dispatchEvent(new CustomEvent('personalisation:ready'));
      } catch (error) {
        console.warn('Personalisation unavailable:', error.message);
      }
    }

    loadFallbackOptions() {
      this.loadFallbackFonts();
      this.loadFallbackColors();

      const placementButtons = this.drawer.querySelectorAll('[data-placement-options] [data-placement]');
      if (placementButtons.length > 1) {
        placementButtons.forEach(btn => btn.classList.remove('active'));
        this.selections.placement = '';
        this.selections.placementCode = '';
      } else if (placementButtons.length === 1) {
        placementButtons[0].classList.add('active');
        this.selections.placement = placementButtons[0].dataset.placement || 'Front';
        this.selections.placementCode = placementButtons[0].dataset.areaCode || '';
      }
      this.updateNavigation();
    }

    loadFallbackFonts() {
      const fontContainer = this.drawer.querySelector('[data-font-options]');
      const fallbackTemplate = document.getElementById('fallback-fonts');
      
      if (fontContainer && fallbackTemplate) {
        fontContainer.innerHTML = fallbackTemplate.innerHTML;
        
        // Re-bind font option click handlers
        this.fontOptions = this.drawer.querySelectorAll('[data-font]');
        this.fontOptions.forEach(option => {
          option.addEventListener('click', () => this.selectFont(option));
        });
        
        // Select first font
        const firstFont = this.drawer.querySelector('[data-font].active') || this.drawer.querySelector('[data-font]');
        if (firstFont) {
          this.selections.font = firstFont.dataset.font;
          this.selections.fontFamily = firstFont.dataset.fontFamily;
          this.selections.fontCode = firstFont.dataset.fontCode || '';
        }
        
      } else {
        console.warn('Could not load fallback fonts - missing container or template');
      }
    }

    loadFallbackColors() {
      const colorContainer = this.drawer.querySelector('[data-color-options]');
      const fallbackTemplate = document.getElementById('fallback-colors');
      
      if (colorContainer && fallbackTemplate) {
        colorContainer.innerHTML = fallbackTemplate.innerHTML;
        
        // Re-bind color option click handlers
        this.colorOptions = this.drawer.querySelectorAll('[data-color]');
        this.colorOptions.forEach(option => {
          option.addEventListener('click', () => this.selectColor(option));
        });
        
        // Select first color
        const firstColor = this.drawer.querySelector('[data-color].active') || this.drawer.querySelector('[data-color]');
        if (firstColor) {
          this.selections.color = firstColor.dataset.color;
          this.selections.colorHex = firstColor.dataset.hex;
        }
        
      } else {
        console.warn('Could not load fallback colors - missing container or template');
      }
    }

    renderApiPlacements(coordinates) {
      const placementContainer = this.drawer.querySelector('[data-placement-options]');
      if (!placementContainer) return;
      
      // Clear existing placement buttons
      placementContainer.innerHTML = '';
      
      // Reset grid classes
      placementContainer.classList.remove('placement-options--single', 'placement-options--two-col', 'placement-options--multi');
      
      // Determine grid layout based on number of options
      if (coordinates.length === 1) {
        placementContainer.classList.add('placement-options--single');
      } else if (coordinates.length === 2) {
        placementContainer.classList.add('placement-options--two-col');
      } else {
        placementContainer.classList.add('placement-options--multi');
      }
      
      // Create buttons from API data
      coordinates.forEach((coord, index) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'placement-option';
        button.dataset.placement = coord.area_name;
        button.dataset.areaCode = coord.area_code;
        button.dataset.coordIndex = index; // Store index to retrieve full data
        button.textContent = coord.area_name;
        
        
        // Add click handler
        button.addEventListener('click', () => this.selectPlacement(button));
        
        placementContainer.appendChild(button);
      });
      
      // Update cached placement options
      this.placementOptions = this.drawer.querySelectorAll('[data-placement]');
      
      // No pre-selection when multiple placements - user must choose
      if (coordinates.length > 1) {
        this.selections.placement = '';
        this.selections.placementCode = '';
      } else if (coordinates.length === 1) {
        const firstBtn = placementContainer.querySelector('.placement-option');
        if (firstBtn) firstBtn.classList.add('active');
        this.selections.placement = coordinates[0]?.area_name || 'Front';
        this.selections.placementCode = coordinates[0]?.area_code || '';
        this.updatePlacementOptions(coordinates[0]);
      }

      // Fetch initial API preview image (with "HB" default text)
      if (this.useApiPlacements && this.selections.placementCode) {
        this.fetchPreviewImage();
      } else {
        this.updatePreviewImage();
      }

      this.updateNavigation();
    }

    // Update fonts, colors, emojis, and char limits based on selected placement
    updatePlacementOptions(coord) {
      
      this.activePlacement = coord;
      // Store current placement data
      this.currentPlacementData = {
        allowedFonts: coord.allowed_fonts || [],
        allowedEmojis: coord.allowed_emojis || [],
        charLimit: coord.char_limit || 12,
        lineLimit: coord.line_limit || 1
      };
      
      // Update character limit
      this.updateCharLimitFromApi(this.currentPlacementData.charLimit);
      
      // Update fonts based on allowed_fonts for this placement
      this.updateFontOptionsFromApi(this.currentPlacementData.allowedFonts);
      
      // Update emojis/motifs based on allowed_emojis for this placement
      this.updateEmojiOptionsFromApi(this.currentPlacementData.allowedEmojis);
    }

  // Update character limit from API
  updateCharLimitFromApi(charLimit) {
    if (!this.textInput) return;
    
    const newLimit = charLimit || 12;
    this.selections.charLimit = newLimit;
    
    // Update input maxlength
    this.textInput.maxLength = newLimit;
    
    // Update display
    if (this.charLimit) {
      this.charLimit.textContent = newLimit;
    }
    
    // Truncate text if over new limit
    if (this.textInput.value.length > newLimit) {
      this.textInput.value = this.textInput.value.substring(0, newLimit);
      this.handleTextInput();
    }
    
    // Filter suggestion chips based on new limit
    this.filterSuggestionsByCharLimit(newLimit);
    
  }

  // Filter suggestion chips to hide those exceeding character limit
  filterSuggestionsByCharLimit(charLimit) {
    if (!this.suggestionChips || this.suggestionChips.length === 0) return;
    
    this.suggestionChips.forEach(chip => {
      const suggestion = chip.dataset.suggestion || chip.textContent.trim();
      if (suggestion.length > charLimit) {
        chip.style.display = 'none';
      } else {
        chip.style.display = '';
      }
    });
  }

  // Update font options based on API allowed_fonts
    updateFontOptionsFromApi(allowedFonts) {
      const fontContainer = this.drawer.querySelector('[data-font-options]');
      if (!fontContainer) {
        return;
      }
      
      if (!allowedFonts || allowedFonts.length === 0) {
        this.loadFallbackFonts();
        return;
      }
      
      // Clear existing font options
      fontContainer.innerHTML = '';
      
      // Create buttons from API font data
      allowedFonts.forEach((font, index) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'font-option' + (index === 0 ? ' active' : '');
        button.dataset.font = font.font_name?.toLowerCase().replace(/\s+/g, '_') || font.font_code;
        button.dataset.fontCode = font.font_code;
        
        // Map API font to CSS font family
        const fontFamily = this.mapApiFontToFamily(font.font_code);
        button.dataset.fontFamily = fontFamily;
        
        // Create preview and name elements
        const preview = document.createElement('span');
        preview.className = 'font-option__preview';
        preview.style.fontFamily = fontFamily;
        preview.textContent = 'Abc';
        
        const name = document.createElement('span');
        name.className = 'font-option__name';
        name.textContent = font.font_name || font.font_code;
        
        // Optional: Add sample image if available
        if (font.font_sample_image) {
          button.dataset.sampleImage = font.font_sample_image;
        }
        
        button.appendChild(preview);
        button.appendChild(name);
        
        // Add click handler
        button.addEventListener('click', () => this.selectFont(button));
        
        fontContainer.appendChild(button);
      });
      
      // Update cached font options
      this.fontOptions = this.drawer.querySelectorAll('[data-font]');
      
      // Select first font by default
      if (allowedFonts.length > 0) {
        const firstFont = allowedFonts[0];
        this.selections.font = firstFont.font_name?.toLowerCase().replace(/\s+/g, '_') || firstFont.font_code;
        this.selections.fontCode = firstFont.font_code;
        this.selections.fontFamily = this.mapApiFontToFamily(firstFont.font_code);
        this.updatePreview();
      }
      
    }

    // Map API font code to CSS font-family
    mapApiFontToFamily(fontCode) {
      const fontMap = {
        'bluebell_2': "'Pinyon Script', cursive",
        'garamond_regular': "'Cormorant Garamond', serif",
        'garmnd': "'Bebas Neue', sans-serif", // Block/Bold Garamond
        'quentin': "'Caveat', cursive", // Handwriting
        'rounded': "'Nunito Sans', sans-serif", // VAG Rounded
        'pinyonscript': "'Pinyon Script', cursive",
        'montserrat': "'Montserrat', sans-serif",
        'bebasneue': "'Bebas Neue', sans-serif",
        'caveat': "'Caveat', cursive",
        'cinzeldecorative': "'Cinzel Decorative', serif"
      };
      return fontMap[fontCode?.toLowerCase()] || "'Cormorant Garamond', serif";
    }

    // Update emoji/motif options based on API allowed_emojis
    updateEmojiOptionsFromApi(allowedEmojis) {
      const emojiContainer = this.drawer.querySelector('[data-emoji-options]');

      // Whitelist: only show these specific emojis/motifs/crests
      // Uses partial matching to allow all colour variants (e.g. "Horse Crest Gold")
      const ALLOWED_NAMES = [
        'horse crest',
        'leaf crest',
        'mini heart',
        'pink bee',
        'painted heart',
        'star',
        'heart solid'
      ];

      // Block any emoji with astrology/zodiac terms in the name
      const BLOCKED_TERMS = [
        'aries', 'taurus', 'gemini', 'cancer', 'leo', 'virgo',
        'libra', 'scorpio', 'sagittarius', 'capricorn', 'aquarius', 'pisces',
        'zodiac', 'astro', 'horoscope'
      ];

      if (allowedEmojis && allowedEmojis.length > 0) {

        allowedEmojis = allowedEmojis.filter(emoji => {
          const name = (emoji.emoji_name || '').toLowerCase();
          const ref = (emoji.emoji_reference || '').toLowerCase().replace(/:/g, '');

          // Block anything with astrology/zodiac terms
          const isBlocked = BLOCKED_TERMS.some(term => name.includes(term) || ref.includes(term));
          if (isBlocked) {
            return false;
          }

          const match = ALLOWED_NAMES.some(allowed => {
            const noSpace = allowed.replace(/\s+/g, '');
            return name.includes(allowed) || ref.includes(noSpace);
          });
          if (!match) {
          }
          return match;
        });

      }

      // If no emojis available for this placement, hide the section
      if (!allowedEmojis || allowedEmojis.length === 0) {
        if (emojiContainer) {
          emojiContainer.style.display = 'none';
        }
        this.allowedEmojis = [];
        return;
      }
      
      // Create container if it doesn't exist
      if (!emojiContainer) {
        this.createEmojiContainer();
      }
      
      const container = this.drawer.querySelector('[data-emoji-options]');
      if (!container) return;
      
      // Show container and clear existing content
      container.style.display = '';
      const listContainer = container.querySelector('.emoji-options__list') || container;
      
      // Clear existing emoji buttons but keep label
      const existingButtons = listContainer.querySelectorAll('.emoji-btn, .motif-btn');
      existingButtons.forEach(btn => btn.remove());
      
      // Reset allowed emojis list
      this.allowedEmojis = [];
      
      // Create buttons from API emoji/motif data
      allowedEmojis.forEach(emoji => {
        // Only show enabled emojis (status === 1)
        if (emoji.status !== 1) return;
        
        const button = document.createElement('button');
        button.type = 'button';
        button.className = emoji.type === 'motiff' ? 'motif-btn' : 'emoji-btn';
        button.dataset.emojiRef = emoji.emoji_reference;
        button.dataset.emojiName = emoji.emoji_name;
        button.title = emoji.emoji_name;
        
        // Store reference for text insertion
        this.allowedEmojis.push(emoji.emoji_reference);
        
        // If there's an image, show it; otherwise show name
        if (emoji.emoji_image) {
          const img = document.createElement('img');
          img.src = emoji.emoji_image;
          img.alt = emoji.emoji_name;
          img.className = 'emoji-btn__image';
          button.appendChild(img);
        } else {
          button.textContent = emoji.emoji_name.substring(0, 2);
        }
        
        // Add click handler to insert emoji reference
        button.addEventListener('click', () => this.insertEmojiReference(emoji.emoji_reference));
        
        listContainer.appendChild(button);
      });
      
      // Update cached emoji buttons
      this.emojiButtons = this.drawer.querySelectorAll('[data-emoji-ref]');
      
    }

    // Create emoji container if it doesn't exist
    createEmojiContainer() {
      // Find the text step panel
      const textStep = 3;
      const textPanel = this.drawer.querySelector(`[data-panel="${textStep}"]`);
      if (!textPanel) return;
      
      const stepContent = textPanel.querySelector('.step-panel__content');
      if (!stepContent) return;
      
      // Check if container already exists
      if (stepContent.querySelector('[data-emoji-options]')) return;
      
      // Create container
      const container = document.createElement('div');
      container.className = 'emoji-options';
      container.dataset.emojiOptions = '';
      
      const label = document.createElement('span');
      label.className = 'emoji-options__label';
      label.textContent = 'Available motifs:';
      
      const list = document.createElement('div');
      list.className = 'emoji-options__list';
      
      container.appendChild(label);
      container.appendChild(list);
      
      // Insert after text input group
      const textInputGroup = stepContent.querySelector('.text-input-group');
      if (textInputGroup) {
        textInputGroup.after(container);
      } else {
        stepContent.prepend(container);
      }
    }

    // Insert emoji reference into text
    insertEmojiReference(emojiRef) {
      if (!this.textInput || !emojiRef) return;
      
      const maxLength = this.selections.charLimit || parseInt(this.textInput.maxLength) || 12;
      const currentText = this.textInput.value;
      
      // Emoji references might count as 1 or more characters based on API char_count
      // For simplicity, check if we have room
      if (currentText.length + 2 <= maxLength) { // Conservative estimate
        const start = this.textInput.selectionStart;
        const end = this.textInput.selectionEnd;
        
        this.textInput.value = currentText.substring(0, start) + emojiRef + currentText.substring(end);
        
        // Move cursor after emoji reference
        const newPosition = start + emojiRef.length;
        this.textInput.setSelectionRange(newPosition, newPosition);
        this.textInput.focus();
        
        this.handleTextInput();
      } else {
        // Show feedback that text is at limit
        this.textInput.classList.add('shake');
        setTimeout(() => this.textInput.classList.remove('shake'), 500);
      }
    }

    // Update color options based on API available_threads
    updateColorOptionsFromApi(availableThreads) {
      const colorContainer = this.drawer.querySelector('[data-color-options]');
      if (!colorContainer) {
        return;
      }
      
      if (!availableThreads || availableThreads.length === 0) {
        this.loadFallbackColors();
        return;
      }
      
      // Clear existing color options
      colorContainer.innerHTML = '';
      
      // Track unique colors (API sometimes returns duplicates)
      const seenColors = new Set();
      
      // Create buttons from API thread data
      availableThreads.forEach((thread, index) => {
        // Skip disabled threads and duplicates
        if (Number(thread.is_enabled) !== 1 || thread.colour_name.toLowerCase() === 'chrome') return;
        const colorKey = thread.colour_code?.toLowerCase();
        if (seenColors.has(colorKey)) return;
        seenColors.add(colorKey);
        
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'color-option' + (index === 0 ? ' active' : '');
        button.dataset.color = thread.colour_name;
        button.dataset.hex = thread.colour_code;
        button.title = thread.colour_name;
        
        // Create swatch
        const swatch = document.createElement('span');
        swatch.className = 'color-option__swatch';
        swatch.style.backgroundColor = thread.colour_code;
        
        // Create name
        const name = document.createElement('span');
        name.className = 'color-option__name';
        name.textContent = thread.colour_name;
        
        button.appendChild(swatch);
        button.appendChild(name);
        
        // Add click handler
        button.addEventListener('click', () => this.selectColor(button));
        
        colorContainer.appendChild(button);
      });
      
      // Update cached color options
      this.colorOptions = this.drawer.querySelectorAll('[data-color]');
      
      // Select first color by default (or recommended color if available)
      const enabledThreads = availableThreads.filter(t => Number(t.is_enabled) === 1);
      if (enabledThreads.length > 0) {
        const firstColor = enabledThreads[0];
        this.selections.color = firstColor.colour_name;
        this.selections.colorHex = firstColor.colour_code;
        this.updatePreview();
      }
      
    }

    updateProgressSteps() {
      // Show/hide progress steps based on total steps
      const progressContainer = this.drawer.querySelector('.progress-steps');
      if (!progressContainer) return;
      
      // This will be handled by CSS/visibility based on total steps
      this.drawer.dataset.totalSteps = this.totalSteps;
    }

    // Fetch preview image from mono API
    async fetchPreviewImage() {
      if (!this.selections.placementCode || !this.selections.fontCode) return false;
      const signature = this.previewSignature();
      const requestId = (this.previewRequestId || 0) + 1;
      this.previewRequestId = requestId;
      this.previewValid = false;
      this.updateNavigation();
      const card = this.drawer.querySelector('.preview-card');
      card.classList.add('is-loading');
      const errorBox = this.drawer.querySelector('[data-personaliser-error]');
      errorBox.hidden = true;
      try {
        const params = new URLSearchParams({shop: SHOP_DOMAIN, product_id: this.productId,
          sample_text: this.selections.text || 'HB', position: this.selections.placementCode,
          font: this.selections.fontCode, colour: this.selections.colorHex.replace('#', '').toLowerCase()});
        const result = await xhrGet(`${PREVIEW_API}?${params}`);
        if (requestId !== this.previewRequestId || signature !== this.previewSignature()) return false;
        if (!result.ok || result.data?.error || !result.data?.encoded_image) throw new Error('Preview unavailable. Please try your selection again.');
        const data = result.data;
        const img = this.drawer.querySelector('[data-preview-image]');
        img.src = `data:image/${data.encoded_image.startsWith('/9j/') ? 'jpeg' : 'png'};base64,${data.encoded_image}`;
        this.previewValid = true;
        this.validPreviewSignature = signature;
        this.previewUrl = data.image_url || data.preview_url || '';
        this.updateFloatingButton();
        return true;
      } catch (error) {
        if (requestId === this.previewRequestId) {
          errorBox.textContent = error.message;
          errorBox.hidden = false;
        }
        return false;
      } finally {
        if (requestId === this.previewRequestId) {
          card.classList.remove('is-loading');
          this.updateNavigation();
        }
      }
    }

    previewSignature() {
      return [this.selections.placementCode, this.selections.fontCode, this.selections.text, this.selections.colorHex].join('|');
    }

    getApiFontCode(fontName) {
      const fontMap = {
        'script': 'pinyonscript',      // Pinyon Script
        'serif': 'garmnd',             // Garamond
        'sans': 'montserrat',          // Montserrat
        'block': 'bebasneue',          // Bebas Neue
        'handwritten': 'caveat',       // Caveat
        'monogram': 'cinzeldecorative' // Cinzel Decorative
      };
      const code = fontMap[fontName?.toLowerCase()] || 'garmnd';
      return code;
    }

    // Debounced preview update to avoid too many API calls
    debouncedPreviewUpdate() {
      clearTimeout(this.previewDebounceTimer);
      this.previewDebounceTimer = setTimeout(() => {
        if (this.useApiPlacements && this.selections.placementCode) {
          this.fetchPreviewImage();
        }
      }, 500);
    }

    // -------------------------------------------------------------------------
    // Drawer Open/Close
    // -------------------------------------------------------------------------

    open() {
      if (!this.ready) return;
      this.returnFocus = document.activeElement;
      this.drawer.setAttribute('aria-hidden', 'false');
      this.drawer.classList.add('is-open');
      this.panel.focus();
      document.body.classList.add('personalisation-drawer-open');
      
      // Reset to step 1
      if (!this.selections.text) {
        this.goToStep(1);
      }
      
      // Refresh preview on open if we have a placement but no preview yet
      if (this.useApiPlacements && this.selections.placementCode) {
        const previewImage = this.drawer.querySelector('[data-preview-image]');
        // If the image is still the original product image, fetch the preview
        if (previewImage && !previewImage.src.startsWith('data:image')) {
          this.fetchPreviewImage();
        }
      }
      
      // Focus text input after animation (only if text step is step 1)
      if (this.totalSteps === 2) {
        setTimeout(() => {
          if (this.textInput) {
            this.textInput.focus();
          }
        }, 400);
      }

      // Start polling for preview visibility
      this.startPreviewCheck();
    }

    close() {
      this.drawer.setAttribute('aria-hidden', 'true');
      this.returnFocus?.focus();
      this.drawer.classList.remove('is-open');
      document.body.classList.remove('personalisation-drawer-open');
      this.stopPreviewCheck();
      if (this.floatingBtn) {
        this.floatingBtn.classList.remove('is-visible');
      }
    }

    isOpen() {
      return this.drawer.classList.contains('is-open');
    }

    // -------------------------------------------------------------------------
    // Step Navigation
    // -------------------------------------------------------------------------

    goToStep(step) {
      if (step < 1 || step > this.totalSteps) return;

      // Validate before moving forward
      if (step > this.currentStep && !this.validateCurrentStep()) {
        return;
      }

      this.currentStep = step;
      this.updatePanels();
      this.updateProgress();
      this.updateNavigation();
      if (step === 3 && this.textInput && this.scrollContent) {
        this.textInput.focus({ preventScroll: true });
        requestAnimationFrame(() => {
          if (this.currentStep !== 3 || !this.isOpen()) return;
          const textPanel = this.textInput.closest('[data-panel]');
          const top = this.scrollContent.scrollTop + textPanel.getBoundingClientRect().top - this.scrollContent.getBoundingClientRect().top - 16;
          this.scrollContent.scrollTo({
            top: Math.max(0, top),
            behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth'
          });
        });
      }
    }

    nextStep() {
      if (this.currentStep < this.totalSteps) {
        this.goToStep(this.currentStep + 1);
      }
    }

    prevStep() {
      if (this.currentStep > 1) {
        this.goToStep(this.currentStep - 1);
      }
    }

    validateCurrentStep() {
      const placementStep = 1;
      const textStep = 3;

      if (this.currentStep === placementStep && !this.selections.placement) {
        const container = this.drawer.querySelector('[data-placement-options]');
        if (container) {
          container.classList.add('shake');
          setTimeout(() => container.classList.remove('shake'), 500);
        }
        return false;
      }

      if (this.currentStep === textStep) {
        if (!this.selections.text.trim()) {
          this.textInput.focus();
          this.textInput.classList.add('shake');
          setTimeout(() => this.textInput.classList.remove('shake'), 500);
          return false;
        }
      }
      return true;
    }

    updatePanels() {
      this.panels.forEach(panel => {
        const panelStep = parseInt(panel.dataset.panel);
        panel.classList.toggle('active', panelStep === this.currentStep);
      });
    }

    updateProgress() {
      this.progressSteps.forEach((step, index) => {
        const stepNum = index + 1;
        step.classList.remove('active', 'completed');
        
        if (stepNum === this.currentStep) {
          step.classList.add('active');
        } else if (stepNum < this.currentStep) {
          step.classList.add('completed');
        }
      });

      // Update progress lines
      this.progressLines.forEach((line, index) => {
        line.classList.toggle('completed', index < this.currentStep - 1);
      });
    }

    updateNavigation() {
      const count = this.savedDesigns.length + (this.selections.text.trim() ? 1 : 0);
      const quantity = Number(new FormData(this.productForm() || document.createElement('form')).get('quantity') || 1);
      const total = Math.max(count, 1) * quantity * Number(this.dataInput.dataset.feePrice || 1500) / 100;
      const price = this.drawer.querySelector('[data-personalization-price]');
      if (price) price.textContent = '+' + new Intl.NumberFormat(undefined, {style: 'currency', currency: this.dataInput.dataset.currency || 'AUD'}).format(total);
      const isFirstStep = this.currentStep === 1;
      const isLastStep = this.currentStep === this.totalSteps;

      if (this.prevButton) {
        this.prevButton.style.display = isFirstStep ? 'none' : 'inline-flex';
      }
      if (this.nextButton) {
        this.nextButton.style.display = isLastStep ? 'none' : 'inline-flex';

        const placementStep = 1;
        const textStep = 3;

        if (this.currentStep === placementStep) {
          const hasPlacement = !!this.selections.placement;
          this.nextButton.disabled = !hasPlacement;
          this.setButtonLabel(this.nextButton, hasPlacement ? 'Continue to Font' : 'Select Placement', hasPlacement);
        } else if (this.currentStep === textStep) {
          const hasText = !!(this.selections.text && this.selections.text.trim());
          this.nextButton.disabled = !hasText;
          this.setButtonLabel(this.nextButton, hasText ? 'Continue to Colour' : 'Enter Your Text', hasText);
        } else {
          this.nextButton.disabled = false;
          this.setButtonLabel(this.nextButton, 'Continue to Text', true);
        }
      }
      const another = this.drawer.querySelector('[data-another-placement]');
      another.hidden = !isLastStep || this.savedDesigns.length + 1 >= this.apiCoordinates?.length;
      another.disabled = !this.previewValid || !this.selections.text.trim();
      if (this.addButton) {
        this.addButton.style.display = isLastStep ? 'inline-flex' : 'none';
        this.addButton.disabled = this.adding || !this.previewValid || this.validPreviewSignature !== this.previewSignature() || !this.selections.text.trim();
      }
    }

    setButtonLabel(button, text, showArrow) {
      const svg = button.querySelector('svg');
      button.textContent = '';
      button.appendChild(document.createTextNode(text + ' '));
      if (svg) {
        svg.style.display = showArrow ? '' : 'none';
        button.appendChild(svg);
      }
    }

    // -------------------------------------------------------------------------
    // Selection Handlers
    // -------------------------------------------------------------------------

    handleTextInput() {
      let text = this.textInput.value;
      
      // Filter out disallowed emojis/special characters
      text = window.HBPersonalisationRules.text(text, this.activePlacement);
      
      // Update input if text was filtered
      if (text !== this.textInput.value) {
        const cursorPos = this.textInput.selectionStart;
        this.textInput.value = text;
        // Try to maintain cursor position
        this.textInput.setSelectionRange(cursorPos - 1, cursorPos - 1);
      }
      
      this.selections.text = text;
      
      // Update character count
      if (this.charCount) {
        this.charCount.textContent = text.length;
        const counter = this.charCount.closest('.char-counter');
        if (counter) {
          counter.classList.toggle('is-limit', text.length >= parseInt(this.textInput.maxLength));
        }
      }

      this.updatePreview();
      this.updateNavigation();
    }

    filterText(text) {
      // If no emojis are allowed, strip all emojis
      if (!this.allowedEmojis || this.allowedEmojis.length === 0) {
        // Remove all emoji characters
        return text.replace(/[\u{1F300}-\u{1F9FF}\u{2600}-\u{26FF}\u{2700}-\u{27BF}\u{1F600}-\u{1F64F}\u{1F680}-\u{1F6FF}\u{1F1E0}-\u{1F1FF}\u{2300}-\u{23FF}\u{2B50}\u{FE00}-\u{FE0F}\u{200D}]/gu, '');
      }
      
      // Build result keeping only allowed emojis and regular characters
      let result = '';
      const chars = [...text]; // Properly split by unicode codepoints
      
      for (let i = 0; i < chars.length; i++) {
        const char = chars[i];
        
        // Check if this is the start of an allowed emoji
        const remaining = chars.slice(i).join('');
        let foundAllowed = false;
        
        for (const emoji of this.allowedEmojis) {
          if (remaining.startsWith(emoji)) {
            result += emoji;
            // Skip the emoji characters (emoji length in chars, not string length)
            i += [...emoji].length - 1;
            foundAllowed = true;
            break;
          }
        }
        
        if (!foundAllowed) {
          // Check if it's an emoji (to filter out)
          const isEmoji = /[\u{1F300}-\u{1F9FF}\u{2600}-\u{26FF}\u{2700}-\u{27BF}\u{1F600}-\u{1F64F}\u{1F680}-\u{1F6FF}\u{1F1E0}-\u{1F1FF}\u{2300}-\u{23FF}\u{2B50}\u{FE00}-\u{FE0F}\u{200D}]/u.test(char);
          
          if (!isEmoji) {
            result += char;
          }
        }
      }
      
      return result;
    }

    selectFont(option) {
      // Remove active from all
      this.fontOptions.forEach(opt => opt.classList.remove('active'));
      option.classList.add('active');

      this.selections.font = option.dataset.font;
      this.selections.fontFamily = option.dataset.fontFamily;
      this.selections.fontCode = option.dataset.fontCode || this.getApiFontCode(option.dataset.font);
      this.updatePreview();
    }

    selectColor(option) {
      // Remove active from all
      this.colorOptions.forEach(opt => opt.classList.remove('active'));
      option.classList.add('active');

      this.selections.color = option.dataset.color;
      this.selections.colorHex = option.dataset.hex;
      this.updatePreview();
    }

    selectPlacement(option) {
      // Remove active from all
      this.placementOptions.forEach(opt => opt.classList.remove('active'));
      option.classList.add('active');

      this.selections.placement = option.dataset.placement;
      this.selections.placementCode = option.dataset.areaCode || '';
      
      // Re-run navigation to enable button and update label
      this.updateNavigation();
      
      
      // Get full coordinate data for this placement from stored API data
      const coordIndex = parseInt(option.dataset.coordIndex);
      if (this.apiCoordinates && !isNaN(coordIndex) && this.apiCoordinates[coordIndex]) {
        const coord = this.apiCoordinates[coordIndex];
        // Update fonts, emojis, and char limits based on this placement
        this.updatePlacementOptions(coord);
      } else {
        // Fallback to static char limit update
        this.updateCharLimit();
      }
      
      // Switch preview image based on placement
      this.updatePreviewImage();
      this.updatePreview();
    }

    updateCharLimit() {
      if (!this.textInput) return;
      
      const placement = this.selections.placement || 'Front';
      const limitFront = parseInt(this.textInput.dataset.limitFront) || 12;
      const limitBack = parseInt(this.textInput.dataset.limitBack) || 20;
      
      const newLimit = placement === 'Back' ? limitBack : limitFront;
      
      // Update input maxlength
      this.textInput.maxLength = newLimit;
      
      // Update display
      if (this.charLimit) {
        this.charLimit.textContent = newLimit;
      }
      
      // Truncate text if over new limit
      if (this.textInput.value.length > newLimit) {
        this.textInput.value = this.textInput.value.substring(0, newLimit);
        this.handleTextInput();
      }
    }

    insertEmoji(emoji) {
      if (!this.textInput || !emoji) return;
      
      const maxLength = parseInt(this.textInput.maxLength) || 12;
      const currentText = this.textInput.value;
      
      // Check if we have room (emojis count as 2 characters typically)
      if (currentText.length + emoji.length <= maxLength) {
        // Insert at cursor position or append
        const start = this.textInput.selectionStart;
        const end = this.textInput.selectionEnd;
        
        this.textInput.value = currentText.substring(0, start) + emoji + currentText.substring(end);
        
        // Move cursor after emoji
        const newPosition = start + emoji.length;
        this.textInput.setSelectionRange(newPosition, newPosition);
        this.textInput.focus();
        
        this.handleTextInput();
      } else {
        // Show feedback that text is at limit
        this.textInput.classList.add('shake');
        setTimeout(() => this.textInput.classList.remove('shake'), 500);
      }
    }

    updatePreviewImage() {
      const previewImage = this.drawer.querySelector('[data-preview-image]');
      if (!previewImage) return;

      // If using API placements, fetch new preview from API
      // The API returns the correct product image with text overlay
      if (this.useApiPlacements && this.selections.placementCode) {
        this.fetchPreviewImage();
        return;
      }

      // Fallback to local front/back image switching
      const frontImage = previewImage.dataset.frontImage;
      const backImage = previewImage.dataset.backImage;

      if (this.selections.placement === 'Back' && backImage) {
        previewImage.src = backImage;
      } else if (frontImage) {
        previewImage.src = frontImage;
      }
    }

    // -------------------------------------------------------------------------
    // Preview
    // -------------------------------------------------------------------------

    updatePreview() {
      this.previewValid = false;
      this.updateNavigation();
      // Use 'HB' as default preview text
      const displayText = this.selections.text || 'HB';
      
      // Only show local text overlay if NOT using API placements
      // When using API, the mono API renders text directly on the image
      if (!this.useApiPlacements) {
        if (this.previewText) {
          this.previewText.textContent = displayText;
          this.previewText.style.fontFamily = this.selections.fontFamily;
          this.previewText.style.color = this.selections.colorHex;
          this.previewText.classList.toggle('has-text', true);
        }

        if (this.previewOverlay) {
          this.previewOverlay.dataset.position = this.selections.placement;
          this.previewOverlay.style.display = 'block';
        }
      }

      // Update summary text
      if (this.previewSummary) {
        if (this.selections.text) {
          const summary = `"${this.selections.text}" in ${this.selections.color} ${this.selections.font} on ${this.selections.placement}`;
          this.previewSummary.textContent = summary;
        } else {
          this.previewSummary.textContent = 'Preview showing "HB" - enter your text above';
        }
      }
      
      // Fetch API-generated preview if using API placements
      // The mono API will render the text on the image server-side
      if (this.useApiPlacements && this.selections.placementCode) {
        this.debouncedPreviewUpdate();
      }

      this.updateFloatingButton();
    }

    // -------------------------------------------------------------------------
    // Add to Cart
    // -------------------------------------------------------------------------

    async addToCart() {
      if (this.adding || !this.ready || !this.selections.text.trim() || !this.previewValid || this.validPreviewSignature !== this.previewSignature()) return;
      const form = this.productForm();
      if (!form || !form.reportValidity()) return;
      const formData = new FormData(form);
      const variantId = Number(formData.get('id'));
      const quantity = Number(formData.get('quantity') || 1);
      this.adding = true;
      this.updateNavigation();
      const errorBox = this.drawer.querySelector('[data-personaliser-error]');
      errorBox.hidden = true;
      try {
        const properties = {};
        for (const [key, value] of formData.entries()) {
          const match = key.match(/^properties\[(.+)\]$/);
          if (match && typeof value === 'string') properties[match[1]] = value;
        }
        const designs = [...this.savedDesigns, {...this.selections}];
        Object.assign(properties, {
          'Personalisation Text': designs.map(d => d.text).join(' / '), Font: designs.map(d => this.capitalizeFirst(d.font)).join(' / '),
          'Thread Colour': designs.map(d => d.color).join(' / '), Placement: designs.map(d => d.placement).join(' / '),
          _hb_personalisation_count: String(designs.length),
          _personalisation_designs: JSON.stringify(designs.map(d => ({text:d.text,font:d.font,font_code:d.fontCode,colour:d.color,placement:d.placement,placement_code:d.placementCode}))),
          _personalised: 'true', _placement_code: this.selections.placementCode, _font_code: this.selections.fontCode
        });
        if (this.previewUrl) properties._image_url = this.previewUrl;
        const product = { id: variantId, quantity, properties };
        const sellingPlan = formData.get('selling_plan');
        if (sellingPlan) product.selling_plan = sellingPlan;
        const items = window.HBPersonalisationFee.buildItems(product, Number(this.dataInput.dataset.embroideryVariantId), crypto.randomUUID(), this.dataInput.dataset.productTitle);
        const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
          method: 'POST', headers: {'Content-Type': 'application/json'}, body: JSON.stringify({items})
        });
        const result = await response.json();
        if (!response.ok) throw new Error(result.description || 'Could not add personalisation. Please try again.');
        this.close();
        this.savedDesigns = [];
        this.renderSavedDesigns();
        this.resetSelections();
        window.location.assign(`${window.Shopify?.routes?.root || '/'}cart`);
      } catch (error) {
        errorBox.textContent = error.message;
        errorBox.hidden = false;
      } finally {
        this.adding = false;
        this.updateNavigation();
      }
    }

    renderSavedDesigns() {
      const list = this.drawer.querySelector('[data-saved-designs]');
      list.replaceChildren();
      this.placementOptions.forEach(option => { option.disabled = this.savedDesigns.some(d => d.placementCode === option.dataset.areaCode); });
      this.savedDesigns.forEach((design, index) => {
        const row = document.createElement('div');
        const text = document.createElement('span');
        text.textContent = `${index + 1}. ${design.text} — ${design.placement} (+$15)`;
        const remove = document.createElement('button');
        remove.type = 'button';
        remove.textContent = 'Remove';
        remove.addEventListener('click', () => { this.savedDesigns.splice(index, 1); this.renderSavedDesigns(); this.updateNavigation(); });
        row.append(text, remove); list.append(row);
      });
    }

    productForm() {
      return document.querySelector(`form.js-product-form[data-product-id="${this.productId}"]`) ||
        document.querySelector(`form[id^="product-form-"][id$="-${this.productId}"]`);
    }

    getSelectedVariantId() {
      // First check if there's a variant selector on the page
      const variantInput = document.querySelector('input[name="id"]:checked, select[name="id"], input[name="id"]');
      if (variantInput && variantInput.value) {
        return parseInt(variantInput.value);
      }

      // Fallback to data attribute
      if (this.dataInput && this.dataInput.dataset.variantId) {
        return parseInt(this.dataInput.dataset.variantId);
      }

      return null;
    }

    async refreshCartDrawer() {
      try {
        const response = await fetch('/?sections=cart-drawer,cart-icon-bubble');
        const sections = await response.json();
        
        // Update cart drawer if it exists
        Object.keys(sections).forEach(sectionId => {
          const element = document.getElementById(`shopify-section-${sectionId}`);
          if (element) {
            element.innerHTML = sections[sectionId];
          }
        });
      } catch (error) {
        // Silently fail - cart will update on next page load
      }
    }

    resetSelections() {
      this.selections.text = '';
      if (this.textInput) {
        this.textInput.value = '';
      }
      if (this.charCount) {
        this.charCount.textContent = '0';
      }
      this.goToStep(1);
      this.updatePreview();
    }

    capitalizeFirst(str) {
      return str.charAt(0).toUpperCase() + str.slice(1);
    }
  }

  // -------------------------------------------------------------------------
  // Trigger Button Component
  // -------------------------------------------------------------------------

  class PersonalisationTrigger extends HTMLElement {
    constructor() {
      super();
      this.button = this.querySelector('button') || this;
      this.button.addEventListener('click', () => this.openDrawer());
    }

    openDrawer() {
      document.dispatchEvent(new CustomEvent('personalisation:open'));
    }
  }

  // Register custom element
  if (!customElements.get('personalisation-trigger')) {
    customElements.define('personalisation-trigger', PersonalisationTrigger);
  }

  // -------------------------------------------------------------------------
  // Linked Embroidery Handler
  // Automatically removes embroidery add-on when parent product is removed
  // -------------------------------------------------------------------------

  function initPersonalisation() {
    if (document.getElementById('personalisation-drawer') && !window.personalisationDrawer) window.personalisationDrawer = new PersonalisationDrawer();
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initPersonalisation);
  else initPersonalisation();
